import { useEffect, useState } from "react";
import {
  DisplayResultMessage,
  RegisterRequest,
  RequestSource,
  StorageOauthUserData,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { cloneDeep } from "lodash";
import { decode } from "js-base64";
import { STORAGE_OAUTH_TEMP_DATA_KEY } from "@shared/common/constants";
import { getUtmUserUuid } from "@shared/utils/misc";

interface UseRegisterFormProps {
  referralCode?: string;
  isVerifyEmail?: boolean;
  displayResultMessage: DisplayResultMessage;
  onSuccess: (email: string, emailValidatorUUID?: string) => void;
  onFinishRequest?: () => void;
  queryParams?: Record<string, string>;
}

const useRegisterForm = ({
  referralCode,
  displayResultMessage,
  isVerifyEmail,
  onSuccess,
  onFinishRequest,
  queryParams,
}: UseRegisterFormProps) => {
  const decodedQueryParams = cloneDeep(queryParams);
  if (decodedQueryParams) {
    Object.entries(decodedQueryParams).map(([key, value]) => {
      decodedQueryParams[key] = ENCODED_PARAMS.includes(key)
        ? decode(value)
        : value;
    });
  }

  const { first_name, last_name, username, image, uuid } =
    decodedQueryParams || {};
  const verifyPersonalDetailsStatus =
    sharedLib.sharedApi.authentication.useVerifyPersonalDetailsQuery(
      uuid
        ? {
            uuid,
            first_name,
            last_name,
            username,
            image,
          }
        : skipToken
    );
  const [telegramUuid, setTelegramUuid] = useState<string>();
  const [register, { isLoading }] =
    sharedLib.sharedApi.authentication.useRegisterMutation();
  const checkReferral = !isVerifyEmail && referralCode;
  const { data: referralDiscount } =
    sharedLib.sharedApi.authentication.useCheckReferralQuery(
      checkReferral ? { referral_code: referralCode } : skipToken
    );
  const [referralClicked] =
    sharedLib.sharedApi.authentication.useReferralClickedMutation();

  const telegramUserData = telegramUuid
    ? (decodedQueryParams as StorageOauthUserData)
    : undefined;

  useEffect(() => {
    if (telegramUserData) {
      sharedLib.storageService.setJsonValue(
        STORAGE_OAUTH_TEMP_DATA_KEY,
        telegramUserData
      );
    }
  }, [telegramUserData]);

  useEffect(() => {
    if (verifyPersonalDetailsStatus.isSuccess) {
      setTelegramUuid(decodedQueryParams?.uuid);
    }
  }, [verifyPersonalDetailsStatus]);

  useEffect(() => {
    if (checkReferral) referralClicked({ referral_code: referralCode });
  }, [checkReferral]);

  const onFinish = async (
    values: Omit<RegisterRequest, "referral_code" | "request_source">
  ) => {
    const utmUserUuid = await getUtmUserUuid?.().catch(() => {});
    const registerRequest: RegisterRequest = {
      ...values,
      request_source: RequestSource.WEBSITE,
      use_captcha: true,
    };
    if (referralCode) {
      registerRequest.referral_code = referralCode;
    }
    if (utmUserUuid) {
      registerRequest.utm_user_uuid = utmUserUuid;
    }
    displayResultMessage(
      register(registerRequest).unwrap(),
      "Registration Complete! Please check your inbox and verify your email before attempting to login into your account.",
      "Registration Failed!"
    )
      .then((response) => {
        onSuccess(values.email, response?.data?.email_validator_uuid);
      })
      .catch(() => {})
      .finally(() => {
        onFinishRequest?.();
      });
  };

  const removeUuid = () => {
    setTelegramUuid(undefined);
    sharedLib.storageService.removeValue(STORAGE_OAUTH_TEMP_DATA_KEY);
  };

  return {
    onFinish,
    isLoading,
    referralDiscount,
    checkReferral,
    telegramUserData,
    telegramUuid,
    removeUuid,
  };
};

export default useRegisterForm;

const ENCODED_PARAMS = ["first_name", "last_name", "username", "image", "uuid"];
